import { useLayoutEffect, useRef } from "react";

export const useAnimatedRowReorder = <RowId extends string | number>() => {
	const containerRef = useRef<HTMLElement | null>(null);
	const rowRefs = useRef(new Map<RowId, HTMLElement>());
	const previousLayout = useRef<{
		scrollTop: number;
		tops: Map<RowId, number>;
	} | null>(null);

	const setRowRef = (rowId: RowId) => (node: HTMLElement | null) => {
		if (node) {
			rowRefs.current.set(rowId, node);
		} else {
			rowRefs.current.delete(rowId);
		}
	};

	const scrollRowIntoView = (
		rowId: RowId,
		behavior: ScrollBehavior = "smooth",
	) => {
		const container = containerRef.current;
		const node = rowRefs.current.get(rowId);

		if (!container || !node) {
			return false;
		}

		const containerRect = container.getBoundingClientRect();
		const nodeRect = node.getBoundingClientRect();
		const offsetWithinContainer =
			container.scrollTop + (nodeRect.top - containerRect.top);

		container.scrollTo({
			top:
				offsetWithinContainer - (container.clientHeight - nodeRect.height) / 2,
			behavior,
		});

		return true;
	};

	const captureLayout = () => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		const tops = new Map<RowId, number>();

		for (const [rowId, node] of rowRefs.current) {
			tops.set(rowId, node.offsetTop);
		}

		previousLayout.current = { scrollTop: container.scrollTop, tops };
	};

	useLayoutEffect(() => {
		const container = containerRef.current;
		const previous = previousLayout.current;

		if (!container || !previous) {
			return;
		}

		previousLayout.current = null;
		container.scrollTop = previous.scrollTop;

		const moved: HTMLElement[] = [];

		for (const [rowId, node] of rowRefs.current) {
			const previousTop = previous.tops.get(rowId);

			if (previousTop === undefined) {
				continue;
			}

			const delta = previousTop - node.offsetTop;

			if (delta === 0) {
				continue;
			}

			node.style.transition = "none";
			node.style.transform = `translateY(${delta}px)`;
			moved.push(node);
		}

		if (moved.length === 0) {
			return;
		}

		const frame = requestAnimationFrame(() => {
			for (const node of moved) {
				node.style.transition = `transform 400ms ease`;
				node.style.transform = "";
			}
		});

		return () => cancelAnimationFrame(frame);
	});

	return { containerRef, setRowRef, captureLayout, scrollRowIntoView };
};
